import { toast } from '@heidi/design-system/toast';
import { logger, sanitizeError } from '@heidi/runtime/logger';
import { type CSATRating } from '@heidi/schema/ml-scribe';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import type { TrackWorkExperienceFeedbackActioned } from '../../../contracts/work-launch-analytics.types';
import { submitWorkExperienceFeedback } from '../../../data/projects/api/workExperienceFeedbackApi';
import {
  workExperienceFeedbackDetailAtom,
  workExperienceFeedbackMarkSubmittedAtom,
  workExperienceFeedbackRatingAtom,
  workExperienceFeedbackRatingSubmittedAtom,
  workExperienceFeedbackResetFormAtom,
  workExperienceFeedbackSnoozeCountAtom,
  workExperienceFeedbackUsefulnessRatingAtom,
} from '../../../data/projects/state/workExperienceFeedbackAtoms';

interface UseWorkExperienceFeedbackSubmissionOptions {
  queryCount: number;
  projectId?: string;
  /** The app's `work_experience_feedback_actioned` emit. Pass a stable function. */
  trackWorkExperienceFeedbackActioned: TrackWorkExperienceFeedbackActioned;
}

interface UseWorkExperienceFeedbackSubmissionResult {
  easeRating: CSATRating | null;
  usefulnessRating: CSATRating | null;
  detail: string;
  isRatingSubmitted: boolean;
  submitEaseRating: (rating: CSATRating) => void;
  submitUsefulnessRating: (rating: CSATRating) => void;
  setDetail: (detail: string) => void;
  submitFeedback: () => Promise<void>;
  isSubmitDisabled: boolean;
  isSubmitting: boolean;
}

export const useWorkExperienceFeedbackSubmission = ({
  queryCount,
  projectId,
  trackWorkExperienceFeedbackActioned,
}: UseWorkExperienceFeedbackSubmissionOptions): UseWorkExperienceFeedbackSubmissionResult => {
  const intl = useIntl();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const easeRating = useAtomValue(workExperienceFeedbackRatingAtom);
  const usefulnessRating = useAtomValue(workExperienceFeedbackUsefulnessRatingAtom);
  const detail = useAtomValue(workExperienceFeedbackDetailAtom);
  const snoozeCount = useAtomValue(workExperienceFeedbackSnoozeCountAtom);
  const isRatingSubmitted = useAtomValue(workExperienceFeedbackRatingSubmittedAtom);

  const setEaseRatingAtom = useSetAtom(workExperienceFeedbackRatingAtom);
  const setUsefulnessRatingAtom = useSetAtom(workExperienceFeedbackUsefulnessRatingAtom);
  const setDetailAtom = useSetAtom(workExperienceFeedbackDetailAtom);
  const setRatingSubmitted = useSetAtom(workExperienceFeedbackRatingSubmittedAtom);
  const markSubmitted = useSetAtom(workExperienceFeedbackMarkSubmittedAtom);
  const resetForm = useSetAtom(workExperienceFeedbackResetFormAtom);

  const submitEaseRating = useCallback(
    (newRating: CSATRating) => {
      setEaseRatingAtom(newRating);
      trackWorkExperienceFeedbackActioned({
        action: 'rating_submitted',
        rating_type: 'ease',
        rating: newRating,
        query_count: queryCount,
        snooze_count: snoozeCount,
        project_id: projectId,
      });
      setRatingSubmitted(true);
    },
    [
      queryCount,
      snoozeCount,
      projectId,
      setEaseRatingAtom,
      setRatingSubmitted,
      trackWorkExperienceFeedbackActioned,
    ]
  );

  const submitUsefulnessRating = useCallback(
    (newRating: CSATRating) => {
      setUsefulnessRatingAtom(newRating);
      trackWorkExperienceFeedbackActioned({
        action: 'rating_submitted',
        rating_type: 'usefulness',
        rating: newRating,
        query_count: queryCount,
        snooze_count: snoozeCount,
        project_id: projectId,
      });
    },
    [
      queryCount,
      snoozeCount,
      projectId,
      setUsefulnessRatingAtom,
      trackWorkExperienceFeedbackActioned,
    ]
  );

  const setDetail = useCallback(
    (newDetail: string) => {
      setDetailAtom(newDetail);
    },
    [setDetailAtom]
  );

  const submitFeedback = useCallback(async () => {
    if (easeRating === null || usefulnessRating === null) {
      return;
    }

    const trimmedComment = (detail ?? '').trim();
    const hasComment = trimmedComment.length > 0;

    setIsSubmitting(true);

    try {
      const response = await submitWorkExperienceFeedback({
        ease_stars: easeRating as number,
        usefulness_stars: usefulnessRating as number,
        feedback: hasComment ? trimmedComment : undefined,
      });
      trackWorkExperienceFeedbackActioned({
        action: 'submitted',
        ease_rating: easeRating as number,
        usefulness_rating: usefulnessRating as number,
        has_comment: hasComment,
        comment_length: trimmedComment.length,
        comment: trimmedComment,
        query_count_at_trigger: queryCount,
        snooze_count_before_submit: snoozeCount,
        feedback_count: response.work_general_feedback_count,
        project_id: projectId,
      });
      markSubmitted();
      resetForm();
      toast({
        tone: 'success',
        title: intl.formatMessage({
          defaultMessage: 'Thanks for your feedback!',
          id: 'yx2np5l6sU',
          description: 'Work experience feedback: success toast title',
        }),
      });
    } catch (error) {
      // A fixed message and the sanitised error: the raw one serialises the
      // request body, which carries the clinician's free-text comment.
      logger.error('Failed to submit Work experience feedback:', {
        error: sanitizeError(error),
      });
      trackWorkExperienceFeedbackActioned({
        action: 'submitted',
        ease_rating: easeRating as number,
        usefulness_rating: usefulnessRating as number,
        has_comment: hasComment,
        comment_length: trimmedComment.length,
        comment: trimmedComment,
        query_count_at_trigger: queryCount,
        snooze_count_before_submit: snoozeCount,
        error: true,
        project_id: projectId,
      });
      toast({
        tone: 'destructive',
        title: intl.formatMessage({
          defaultMessage: 'Failed to submit feedback',
          id: 'MoyIINwYVk',
          description: 'Work experience feedback: error toast title',
        }),
        description: intl.formatMessage({
          defaultMessage: 'Please try again later.',
          id: 'hjbyXg4q3v',
          description: 'Work experience feedback: error toast description',
        }),
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    easeRating,
    usefulnessRating,
    detail,
    queryCount,
    snoozeCount,
    projectId,
    markSubmitted,
    resetForm,
    intl,
    trackWorkExperienceFeedbackActioned,
  ]);

  return {
    easeRating,
    usefulnessRating,
    detail,
    isRatingSubmitted,
    submitEaseRating,
    submitUsefulnessRating,
    setDetail,
    submitFeedback,
    isSubmitDisabled: easeRating === null || usefulnessRating === null,
    isSubmitting,
  };
};
